import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet } from "../../lib";

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1200, margin: "24px auto 0", width: "100%" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: { fontSize: 19, fontWeight: 500 },
  panel: { padding: theme.spacing(2) },
  cell: { fontSize: 12.5, padding: "6px 10px", verticalAlign: "top" },
  when: { whiteSpace: "nowrap" },
  field: { fontWeight: 600 },
  from: { color: theme.palette.text.secondary, wordBreak: "break-word" },
  to: { wordBreak: "break-word" },
  redacted: { color: theme.palette.text.secondary, fontStyle: "italic" },
  type: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 10.5,
    fontWeight: 700,
    color: "#315179",
    background: "rgba(49,81,121,0.10)",
    textTransform: "uppercase",
  },
  empty: { padding: theme.spacing(5, 2), textAlign: "center", color: theme.palette.text.secondary },
  muted: { color: theme.palette.text.secondary },
}));

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};

/** Values can be long HTML blobs; the table should stay readable. */
const clip = (v, n = 220) => {
  const s = String(v ?? "");
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

/**
 * Change history for one order, on its own page — the same arrangement as a
 * watch's history rather than a slab at the bottom of the detail screen.
 */
export function OrderHistoryPage() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await authGet(`/orders/${id}/history`);
    if (err) setError(err.message || "Could not load history.");
    else setRows(data?.rows ?? []);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box className={classes.page}>
      <div className={classes.head}>
        <Button size="small" variant="outlined" onClick={() => history.push(`/orders/${id}`)}>
          ← Order {id}
        </Button>
        <Typography className={classes.title}>Change History</Typography>
        {!loading && (
          <span className={classes.muted} style={{ fontSize: 13 }}>
            {rows.length} {rows.length === 1 ? "entry" : "entries"}
          </span>
        )}
      </div>

      <Paper className={classes.panel}>
        {loading ? (
          <div className={classes.empty}>
            <CircularProgress size={26} />
          </div>
        ) : error ? (
          <Typography style={{ color: "#c62828" }}>{error}</Typography>
        ) : !rows.length ? (
          <div className={classes.empty}>
            <Typography variant="body2">Nothing has been changed on this order.</Typography>
          </div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.cell} style={{ width: 165 }}>Changed</TableCell>
                <TableCell className={classes.cell} style={{ width: 100 }}>User</TableCell>
                <TableCell className={classes.cell} style={{ width: 90 }}>Type</TableCell>
                <TableCell className={classes.cell} style={{ width: 170 }}>Field</TableCell>
                <TableCell className={classes.cell}>From</TableCell>
                <TableCell className={classes.cell}>To</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((entry) =>
                // One row per changed field, so the table stays flat and
                // sortable by eye rather than nesting a table per entry.
                entry.changes.map((c, n) => (
                  <TableRow key={`${entry.log_order_id}-${n}`}>
                    <TableCell className={`${classes.cell} ${classes.when}`}>
                      {n === 0 ? when(entry.change_time) : ""}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {n === 0 ? entry.username : ""}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {n === 0 && entry.type && <span className={classes.type}>{entry.type}</span>}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.field}`}>{c.field}</TableCell>
                    {c.redacted ? (
                      <TableCell className={`${classes.cell} ${classes.redacted}`} colSpan={2}>
                        changed — not viewable
                      </TableCell>
                    ) : (
                      <>
                        <TableCell className={`${classes.cell} ${classes.from}`}>
                          {c.from === null || c.from === "" ? (
                            <em className={classes.muted}>n/a</em>
                          ) : (
                            clip(c.from)
                          )}
                        </TableCell>
                        <TableCell className={`${classes.cell} ${classes.to}`}>
                          {clip(c.to)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                )),
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}

export default OrderHistoryPage;
